import React, { useState, useEffect } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  Typography,
  Grid,
  Autocomplete,
  TextField,
  List,
  ListItem,
  ListItemText,
  Button,
  Checkbox,
  Avatar,
  InputAdornment,
  FormControlLabel,
  Chip,
  Box,
} from "@mui/material";
import { DateField, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { request } from "../../../interfaces/networkManager";
import Error from "../../../components/Error/Error";
import "dayjs/locale/fr";
import AccountCircle from "@mui/icons-material/AccountCircle";
import { UserDTO } from "../../../models/userDTO";
import { RoleDTO } from "../../../models/roleDTO";
import { Invoice } from "../../../models/invoice";
import { AutoAwesome, CheckCircle } from "@mui/icons-material";

type Props = {
  user: UserDTO;
  postUpdatedUser: (
    user: UserDTO,
    beginOfferDate: Dayjs | null,
    endOfferDate: Dayjs | null
  ) => void;
  lastInvoiceUser: Invoice | null;
};

const beginningDateLabel = "Date de début";
const endDateLabel = "Date de fin";

const UserDetails: React.FC<Props> = ({
  user,
  postUpdatedUser,
  lastInvoiceUser,
}) => {
  const [userToUpdate, setUserToUpdate] = useState<UserDTO>(user);
  const [checkedSendEmailNewPassword, setCheckedSendEmailNewPassword] =
    useState<boolean>(false);
  const [roles, setRoles] = useState<RoleDTO[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [checked, setChecked] = React.useState(false);
  const [beginOfferDate, setBeginOfferDate] = React.useState<Dayjs | null>(
    dayjs()
  );
  const [endOfferDate, setEndOfferDate] = React.useState<Dayjs | null>(
    dayjs().add(3, "month")
  );
  const [applications, setApplications] = useState<
    { id: number; name: string }[]
  >([]);

  useEffect(() => {
    setUserToUpdate(user);
    setApplications([]);
    if (user.breeder?.farms[0]?.subscription) {
      const applicationsAuthorized: any[] = [];
      if (
        user.breeder?.farms[0]?.subscription?.permissions.find(
          (permission: { id: number; code: string }) =>
            permission.code === "PHMANAGER_ACCESS"
        )
      )
        applicationsAuthorized.push({ id: 1, name: "PH Manager" });
      if (
        user.breeder?.farms[0]?.subscription?.permissions.find(
          (permission: { id: number; code: string }) =>
            permission.code === "ILLICOW_ACCESS"
        )
      )
        applicationsAuthorized.push({ id: 2, name: "illicow" });
      if (
        user.breeder?.farms[0]?.subscription?.permissions.find(
          (permission: { id: number; code: string }) =>
            permission.code === "PHADN_ACCESS"
        )
      )
        applicationsAuthorized.push({ id: 3, name: "PH ADN" });
      if (
        user.breeder?.farms[0]?.subscription?.permissions.find(
          (permission: { id: number; code: string }) =>
            permission.code === "GUIDE_TAUREAUX_ACCESS"
        )
      )
        applicationsAuthorized.push({ id: 4, name: "Guide Taureaux" });
      setApplications(applicationsAuthorized);
    }
  }, [user]);

  useEffect(() => {
    request<RoleDTO[]>("/roles/")
      .then((response: RoleDTO[]) => {
        if (!response.length)
          return setError("Erreur lors de la récupération des roles");
        setRoles(response);
      })
      .catch((error) => {
        setError("Erreur lors de la récupération des roles");
      });
  }, []);

  const onClickRole = (value: string) => {
    const roleToSaved = roles.find((role) => role.name === value) || null;
    if (roleToSaved && userToUpdate)
      setUserToUpdate({ ...userToUpdate, role: roleToSaved });
    // Fermer la liste déroulante
    const input = document.getElementById("search-autocomplete");
    if (input) {
      input.blur();
    }
  };

  const callWBToSave = () => {
    userToUpdate.sendNewPasswordMail = checkedSendEmailNewPassword;
    if (!checked) postUpdatedUser(userToUpdate, null, null);
    else postUpdatedUser(userToUpdate, beginOfferDate, endOfferDate);
    setChecked(false);
  };

  const isUserEqualToUserToUpdate = () => {
    return JSON.stringify(user) !== JSON.stringify(userToUpdate) ||
      checked ||
      userToUpdate?.password?.length > 0
      ? false
      : true;
  };

  // Gérer le clic sur l'Avatar
  const handleAvatarClick = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";

    // Gérer la sélection de l'image depuis l'explorateur de fichiers
    input.onchange = async (e: any) => {
      const file = e.target.files[0];

      if (file) {
        const reader = new FileReader();
        reader.readAsDataURL(file);

        // Gérer la lecture du fichier en base64
        reader.onload = async () => {
          setUserToUpdate({
            ...userToUpdate,
            picture: reader.result as string,
          });
        };
      }
    };

    input.click();
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return new Intl.DateTimeFormat("fr-FR", options).format(date);
  };

  const generateRandomPassword = () => {
    const lowercaseChars = "abcdefghijklmnopqrstuvwxyz";
    const uppercaseChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const digitChars = "0123456789";
    const specialChars = "!@$&*_+.?";
    const allChars =
      lowercaseChars + uppercaseChars + digitChars + specialChars;
    let password = "";

    password +=
      lowercaseChars[Math.floor(Math.random() * lowercaseChars.length)];
    password +=
      uppercaseChars[Math.floor(Math.random() * uppercaseChars.length)];
    password += digitChars[Math.floor(Math.random() * digitChars.length)];
    password += specialChars[Math.floor(Math.random() * specialChars.length)];

    for (let i = 4; i < 13; i++) {
      const randomIndex = Math.floor(Math.random() * allChars.length);
      password += allChars[randomIndex];
    }

    password = password
      .split("")
      .sort(() => 0.5 - Math.random())
      .join("");
    setUserToUpdate({ ...userToUpdate, password: password });
  };

  return (
    <Grid container sx={{ p: 2 }} spacing={2}>
      {error && <Error message={error} />}
      <Grid size={{ xs: 3 }}>
        <Avatar
          alt={userToUpdate?.displayName}
          sx={{ width: 150, height: 150, cursor: "pointer" }}
          src={userToUpdate?.picture || undefined}
          onClick={handleAvatarClick}
        />
      </Grid>
      <Grid size={{ xs: 9 }}>
        <Typography variant="h6">
          Nom de l'utilisateur : {user?.displayName}
        </Typography>
        <Grid container sx={{ pt: 2, pb: 2, alignItems: "center" }}>
          <Grid size={{ xs: 2 }}>
            <Typography variant="body1">Statut : </Typography>
          </Grid>
          <Grid size={{ xs: 10 }}>
            <Autocomplete
              id="search-autocomplete"
              options={roles?.map((option) => option?.name) || []}
              value={userToUpdate.role.name}
              getOptionLabel={(option) => option}
              isOptionEqualToValue={(opt, val) => opt === val}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Type d'utilisateur"
                  variant="outlined"
                />
              )}
              renderOption={(props, option: string) => (
                <List style={{ cursor: "pointer" }} key={option}>
                  <ListItem onClick={() => onClickRole(option)}>
                    <ListItemText primary={option} />
                  </ListItem>
                </List>
              )}
            />
          </Grid>
        </Grid>
        <Grid container sx={{ pt: 2, pb: 2, alignItems: "center" }}>
          <Grid size={{ xs: 2 }}>
            <Typography variant="body1">Email : </Typography>
          </Grid>
          <Grid size={{ xs: 10 }}>
            <TextField
              label="Email"
              value={userToUpdate.email}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setUserToUpdate({ ...userToUpdate, email: event.target.value });
              }}
              variant="outlined"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircle />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ pt: 2, pb: 2, alignItems: "center" }}>
          <Grid size={{ xs: 2 }}>
            <Typography variant="body1">Changer le mot de passe : </Typography>
          </Grid>
          <Grid size={{ xs: 8 }}>
            <TextField
              label="Nouveau mot de passe"
              value={userToUpdate.password ? userToUpdate.password : ""}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setUserToUpdate({
                  ...userToUpdate,
                  password: event.target.value,
                });
              }}
              variant="outlined"
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={generateRandomPassword}
              fullWidth
              startIcon={<AutoAwesome />}
            >
              Générer
            </Button>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControlLabel
              control={
                <Checkbox
                  disabled={
                    userToUpdate.password === "" ||
                    userToUpdate.password === undefined
                  }
                  checked={checkedSendEmailNewPassword}
                  onChange={(e) => {
                    setCheckedSendEmailNewPassword(e.target.checked);
                  }}
                />
              }
              label="Envoi d'un email avec le nouveau mot de passe à l'utilisateur"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <p>
              <em>
                * Si le champ mot de passe est vide, cela ne change pas le mot
                de passe actuel
              </em>
            </p>
          </Grid>
        </Grid>
        {user?.breeder?.farms && user.breeder.farms.length > 0 && (
          <Grid container sx={{ pt: 2, pb: 2, alignItems: "center" }}>
            <Grid size={{ xs: 2 }}>
              <Typography variant="body1">
                Applications autorisées :{" "}
              </Typography>
            </Grid>
            <Grid size={{ xs: 10 }}>
              {applications.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {applications.map((app) => (
                    <Chip
                      key={app.id}
                      label={app.name}
                      icon={<CheckCircle />}
                      color="success"
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Aucune application autorisée
                </Typography>
              )}
            </Grid>
          </Grid>
        )}
        {userToUpdate.role.id === 4 ||
        userToUpdate.role.id === 5 ||
        userToUpdate.role.id === 6 ||
        userToUpdate.role.id === 7 ? (
          <Grid container sx={{ pt: 2, pb: 2, alignItems: "center" }}>
            <Grid size={{ xs: 2 }}>
              <Typography variant="body1">Dernière facture : </Typography>
            </Grid>
            <Grid size={{ xs: 10 }}>
              <Typography variant="body1">
                {lastInvoiceUser !== null &&
                Object.keys(lastInvoiceUser).length > 0
                  ? formatDate(lastInvoiceUser.date)
                  : "Aucune facture"}
              </Typography>
            </Grid>
          </Grid>
        ) : null}
        <Grid container sx={{ alignItems: "center" }}>
          {/* Si l'utilisateur est un prospect */}
          {user.role.id === 8 ? (
            user.discoveryOfferActivationDate &&
            user.discoveryOfferClosingDate ? (
              <Grid size={{ xs: 12 }} sx={{ pb: 2 }}>
                <Grid container>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "1rem",
                    }}
                  >
                    <Typography variant="body1">
                      A souscrit à l'offre découverte depuis :
                    </Typography>
                  </div>
                  <Grid size={{ xs: 12 }}>
                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      adapterLocale="fr"
                    >
                      <div style={{ display: "flex", gap: "16px" }}>
                        <DateField
                          label={beginningDateLabel}
                          value={dayjs(
                            userToUpdate.discoveryOfferActivationDate
                          )}
                          onChange={(newValue) => {
                            if (newValue) {
                              const valueConverted =
                                dayjs(newValue).format("YYYY-MM-DD");
                              setUserToUpdate({
                                ...userToUpdate,
                                discoveryOfferActivationDate: valueConverted,
                              });
                            }
                          }}
                          format="LL"
                        />
                        <DateField
                          label={endDateLabel}
                          value={dayjs(userToUpdate.discoveryOfferClosingDate)}
                          onChange={(newValue) => {
                            if (newValue) {
                              const valueConverted =
                                dayjs(newValue).format("YYYY-MM-DD");
                              setUserToUpdate({
                                ...userToUpdate,
                                discoveryOfferClosingDate: valueConverted,
                              });
                            }
                          }}
                          format="LL"
                        />
                      </div>
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              </Grid>
            ) : (
              <Grid size={{ xs: 12 }} sx={{ pb: 2 }}>
                <Grid container sx={{ alignItems: "center" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <Typography>Souscrire à l’offre découverte : </Typography>
                    <Checkbox
                      checked={checked}
                      onClick={(event: any) => {
                        setChecked(event.target.checked);
                      }}
                    />
                  </div>
                  {checked && (
                    <Grid size={{ xs: 12 }}>
                      <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale="fr"
                      >
                        <DateField
                          label={beginningDateLabel}
                          value={beginOfferDate}
                          onChange={(newValue) => setBeginOfferDate(newValue)}
                          format="LL"
                          sx={{ mr: 2 }}
                        />
                        <DateField
                          label={endDateLabel}
                          value={endOfferDate}
                          onChange={(newValue) => setEndOfferDate(newValue)}
                          format="LL"
                        />
                      </LocalizationProvider>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )
          ) : null}
          <Grid size={{ xs: 12 }}>
            <Button
              variant="contained"
              color="primary"
              disabled={isUserEqualToUserToUpdate()}
              onClick={callWBToSave}
            >
              Sauvegarder
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default UserDetails;
